// eslint-disable-next-line import/prefer-default-export
import { IntlShape, MessageDescriptor } from 'react-intl';

/* If more date related operations are required then please replace this file
 * with an implementation of a date format package
 * https://www.npmjs.com/package/dateformat
 * https://www.npmjs.com/package/moment
 */

const messages: Record<string, MessageDescriptor> = {
  am: {
    id: 'dateHelper.am',
    description: '12 hour timestamp for AM',
  },
  pm: {
    id: 'dateHelper.pm',
    description: '12 hour timestamp for PM',
  },
  sunday: {
    id: 'dateHelper.sunday.short',
    description: 'Three char string for Sunday',
  },
  monday: {
    id: 'dateHelper.monday.short',
    description: 'Three char string for Monday',
  },
  tuesday: {
    id: 'dateHelper.tuesday.short',
    description: 'Three char string for Tuesday',
  },
  wednesday: {
    id: 'dateHelper.wednesday.short',
    description: 'Three char string for Wednesday',
  },
  thursday: {
    id: 'dateHelper.thursday.short',
    description: 'Three char string for Thursday',
  },
  friday: {
    id: 'dateHelper.friday.short',
    description: 'Three char string for Friday',
  },
  saturday: {
    id: 'dateHelper.saturday.short',
    description: 'Three char string for Saturday',
  },
  fullSunday: {
    id: 'dateHelper.sunday.long',
    description: 'Three char string for Sunday',
  },
  fullMonday: {
    id: 'dateHelper.monday.long',
    description: 'Three char string for Monday',
  },
  fullTuesday: {
    id: 'dateHelper.tuesday.long',
    description: 'Three char string for Tuesday',
  },
  fullWednesday: {
    id: 'dateHelper.wednesday.long',
    description: 'Three char string for Wednesday',
  },
  fullThursday: {
    id: 'dateHelper.thursday.long',
    description: 'Three char string for Thursday',
  },
  fullFriday: {
    id: 'dateHelper.friday.long',
    description: 'Three char string for Friday',
  },
  fullSaturday: {
    id: 'dateHelper.saturday.long',
    description: 'Three char string for Saturday',
  },
  january: {
    id: 'dateHelper.january.long',
    description: 'Full string for January',
  },
  february: {
    id: 'dateHelper.february.long',
    description: 'Full string for February',
  },
  march: {
    id: 'dateHelper.march.long',
    description: 'Full string for March',
  },
  april: {
    id: 'dateHelper.april.long',
    description: 'Full string for April',
  },
  may: {
    id: 'dateHelper.may.long',
    description: 'Full string for May',
  },
  june: {
    id: 'dateHelper.june.long',
    description: 'Full string for June',
  },
  july: {
    id: 'dateHelper.july.long',
    description: 'Full string for July',
  },
  august: {
    id: 'dateHelper.august.long',
    description: 'Full string for August',
  },
  september: {
    id: 'dateHelper.september.long',
    description: 'Full string for September',
  },
  october: {
    id: 'dateHelper.october.long',
    description: 'Full string for October',
  },
  november: {
    id: 'dateHelper.november.long',
    description: 'Full string for November',
  },
  december: {
    id: 'dateHelper.december.long',
    description: 'Full string for December',
  },
};

export const parseDate = (date: string | number | Date): Date => {
  if (!date) {
    throw new Error('No date provided');
  }
  return new Date(date);
};

const ddmmyy = (separator: string) => (input: string | number | Date): string => {
  const date = parseDate(input);
  return [date.getDate(), date.getMonth() + 1, date.getFullYear()].join(
    separator,
  );
};

const mmddyy = (separator: string) => (input: string | number | Date): string => {
  const date = parseDate(input);
  return [date.getMonth() + 1, date.getDate(), date.getFullYear()].join(
    separator,
  );
};

const formats: Record<string, (input: string | number | Date) => string> = {
  'dd-mm-yy': ddmmyy('-'),
  'dd/mm/yy': ddmmyy('/'),
  'mm/dd/yy': mmddyy('/'),
  'mm-dd-yy': mmddyy('-'),
};

export const formatDate = (date: string | number | Date, format: string = 'mm/dd/yy'): string => {
  const formatter = formats[format];
  return formatter(date);
};

export const isSameDate = (date1: Date, date2: Date): boolean => {
  try {
    return formatDate(date1) === formatDate(date2);
  } catch {
    // no-op
  }
  return false;
};

export const dayDiff = (date1: Date, date2: Date): number => {
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(utc2 / oneDay) - Math.floor(utc1 / oneDay);
};

export const getWeekDayShortString = (intl: IntlShape, date: Date): string | null => {
  const day = date.getDay();
  const weekday = new Array(7);
  weekday[0] = intl.formatMessage(messages.sunday);
  weekday[1] = intl.formatMessage(messages.monday);
  weekday[2] = intl.formatMessage(messages.tuesday);
  weekday[3] = intl.formatMessage(messages.wednesday);
  weekday[4] = intl.formatMessage(messages.thursday);
  weekday[5] = intl.formatMessage(messages.friday);
  weekday[6] = intl.formatMessage(messages.saturday);

  return weekday[day] || null;
};

export const getWeekDayFullString = (intl: IntlShape, date: Date): string | null => {
  const day = date.getDay();
  const weekday = new Array(7);
  weekday[0] = intl.formatMessage(messages.fullSunday);
  weekday[1] = intl.formatMessage(messages.fullMonday);
  weekday[2] = intl.formatMessage(messages.fullTuesday);
  weekday[3] = intl.formatMessage(messages.fullWednesday);
  weekday[4] = intl.formatMessage(messages.fullThursday);
  weekday[5] = intl.formatMessage(messages.fullFriday);
  weekday[6] = intl.formatMessage(messages.fullSaturday);

  return weekday[day] || null;
};

export const getMonthString = (intl: IntlShape, date: Date): string | null => {
  const month = date.getMonth();
  const monthNames = new Array(12);
  monthNames[0] = intl.formatMessage(messages.january);
  monthNames[1] = intl.formatMessage(messages.february);
  monthNames[2] = intl.formatMessage(messages.march);
  monthNames[3] = intl.formatMessage(messages.april);
  monthNames[4] = intl.formatMessage(messages.may);
  monthNames[5] = intl.formatMessage(messages.june);
  monthNames[6] = intl.formatMessage(messages.july);
  monthNames[7] = intl.formatMessage(messages.august);
  monthNames[8] = intl.formatMessage(messages.september);
  monthNames[9] = intl.formatMessage(messages.october);
  monthNames[10] = intl.formatMessage(messages.november);
  monthNames[11] = intl.formatMessage(messages.december);

  return monthNames[month] || null;
};

export const twelveHrFormat = (date: Date): string => {
  let hours = date.getHours();
  let minutes: string | number = date.getMinutes();

  hours %= 12;
  hours = hours || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${hours}:${minutes}`;
};

export const formatAMPM = (intl: IntlShape, date: Date): string => {
  const hours = date.getHours();

  return hours >= 12
    ? intl.formatMessage(messages.pm, {
        time: twelveHrFormat(date),
      })
    : intl.formatMessage(messages.am, {
        time: twelveHrFormat(date),
      });
};
